import { fromJS } from 'immutable';

export enum StripePaymentStatus {
  Success = 'succeeded',
  Failure = 'failed',
  Pending = 'pending',
}

export enum MarkAsPaidStatus {
  Success = 'marked_as_paid',
  NotPaid = '',
  Void = 'void',
}

export enum RefundReason {
  RequestByCustomer = 'requested_by_customer',
  Duplicate = 'duplicate',
  Fraudulent = 'fraudulent',
  Expired = 'expired',
}

export enum ChargeCategory {
  DISCOUNT = 'discount',
  WARRANTY = 'warranty',
  FUEL = 'fuel',
  PROTECTION_PLAN = 'protection_plan',
  RENTAL_FEES = 'rental_fee',
  DAMAGE = 'damage',
  INFRACTION = 'infraction',
  TOLL = 'toll',
  OTHER = 'other',
}

/* eslint-disable quote-props */
export const DEFAULT_CHARGE_TRANSLATIONS = {
  false: 'no',
  true: 'yes',
};

export const PAID_TRANSLATIONS = {
  false: 'no',
  true: 'yes',
};

export enum CHARGE_CATEGORY {
  discount = 'discount',
  warranty = 'warranty',
  fuel = 'fuel',
  protection_plan = 'protection_plan',
  rental_fee = 'rental_fee',
  damage = 'damage',
  infraction = 'infraction',
  toll = 'toll',
  other = 'other',
}

export const CHARGE_CATEGORY_TRANSLATIONS = {
  1: 'views.FleetSettings.Charges.initial',
  2: 'views.FleetSettings.Charges.rental',
  3: 'views.FleetSettings.Charges.additional',
  additional: 'views.FleetSettings.Charges.additional',
  initial: 'views.FleetSettings.Charges.initial',
  rental: 'views.FleetSettings.Charges.rental',
} as const;

export enum PER_UNIT {
  // These need to match the CHARGE_QUANTITY_UNITS constants in the BE
  notApplicable = '',
  liters = 'L',
  day = 'day',
  kilometers = 'km',
  miles = 'mi',
  ukgallons = 'uk-gal', // Not yet supported by BE.
  usgallons = 'us-gal',
  eighths = 'eighths',
}

export const AUTOFILLABLE_UNITS = [
  PER_UNIT.day,
  PER_UNIT.kilometers,
  PER_UNIT.liters,
  PER_UNIT.miles,
  PER_UNIT.usgallons,
  PER_UNIT.ukgallons,
  PER_UNIT.eighths,
] as PER_UNIT[];

export const PER_UNIT_TRANSLATIONS = {
  notApplicable: 'notApplicable',
  L: 'views.FleetSettings.Charges.liters',
  day: 'views.FleetSettings.Charges.day',
  km: 'views.FleetSettings.Charges.kilometers',
  mi: 'views.FleetSettings.Charges.miles',
  'uk-gal': 'views.FleetSettings.Charges.gallons',
  'us-gal': 'views.FleetSettings.Charges.gallons',
  eighths: 'views.FleetSettings.Charges.eighths',
} as const;

export const RATE_UNIT_TRANSLATIONS = {
  notApplicable: 'notApplicable',
  L: 'views.FleetSettings.Charges.rates.liters',
  day: 'views.FleetSettings.Charges.rates.day',
  km: 'views.FleetSettings.Charges.rates.kilometers',
  mi: 'views.FleetSettings.Charges.rates.miles',
  'uk-gal': 'views.FleetSettings.Charges.rates.gallons',
  'us-gal': 'views.FleetSettings.Charges.rates.gallons',
  eighths: 'views.FleetSettings.Charges.rates.eighths',
} as const;

export const UNITS = {
  price: '$',
  percent: '%',
} as const;

/* The following are used by `ChargesTable.FormRow.subcomponent.js` of the ChargeTable component */

export const DROPDOWN_FORMATTED_PER_UNIT_OPTIONS = fromJS([
  // These are all base units which are not volume or distance
  {
    id: 'notApplicable',
    renderedValue: {
      translationId: PER_UNIT_TRANSLATIONS.notApplicable,
    },
    value: null,
  },
  {
    id: 'day',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS.day },
    value: PER_UNIT.day,
  },
]);

export const DROPDOWN_FORMATTED_PER_VOLUME_OPTIONS = fromJS([
  // These are all possible volume units, to be selected based on dealership settings
  {
    id: 'L',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS.L },
    value: PER_UNIT.liters,
  },
  {
    id: 'us-gal',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS['us-gal'] },
    value: PER_UNIT.usgallons,
  },
  {
    id: 'uk-gal',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS['uk-gal'] },
    value: PER_UNIT.ukgallons,
  },
  {
    id: 'eighths',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS.eighths },
    value: PER_UNIT.eighths,
  },
]);

export const DROPDOWN_FORMATTED_PER_DISTANCE_OPTIONS = fromJS([
  // These are all possible distance units, to be selected based on dealership settings
  {
    id: 'km',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS.km },
    value: PER_UNIT.kilometers,
  },
  {
    id: 'mi',
    renderedValue: { translationId: PER_UNIT_TRANSLATIONS.mi },
    value: PER_UNIT.miles,
  },
]);

export const DROPDOWN_FORMATTED_DEFAULT_CHARGE_OPTIONS = fromJS([
  {
    id: 'true',
    renderedValue: {
      translationId: DEFAULT_CHARGE_TRANSLATIONS.true,
    },
    value: true,
  },
  {
    id: 'false',
    renderedValue: {
      translationId: DEFAULT_CHARGE_TRANSLATIONS.false,
    },
    value: false,
  },
]);

/* The following are used by `ChargeListItem.component.js` of the Agreement View */

type PerUnitOptionType = {
  key: string;
  label: { translationId: string };
  value: PER_UNIT | null;
};

export const CHARGES_PER_UNIT_OPTIONS: PerUnitOptionType[] = [
  // These are all base units which are not volume or distance
  {
    key: 'notApplicable',
    label: { translationId: PER_UNIT_TRANSLATIONS.notApplicable },
    value: null,
  },
  {
    key: 'day',
    label: { translationId: PER_UNIT_TRANSLATIONS.day },
    value: PER_UNIT.day,
  },
];

export const CHARGES_PER_VOLUME_OPTIONS = [
  // These are all possible volume units, to be selected based on dealership settings
  {
    key: 'us-gal',
    label: { translationId: PER_UNIT_TRANSLATIONS['us-gal'] },
    value: PER_UNIT.usgallons,
  },
  {
    key: 'uk-gal',
    label: { translationId: PER_UNIT_TRANSLATIONS['uk-gal'] },
    value: PER_UNIT.ukgallons,
  },
  {
    key: 'L',
    label: { translationId: PER_UNIT_TRANSLATIONS.L },
    value: PER_UNIT.liters,
  },
];

export const CHARGES_PER_DISTANCE_OPTIONS = [
  // These are all possible distance units, to be selected based on dealership settings
  {
    key: 'mi',
    label: { translationId: PER_UNIT_TRANSLATIONS.mi },
    value: PER_UNIT.miles,
  },
  {
    key: 'km',
    label: { translationId: PER_UNIT_TRANSLATIONS.km },
    value: PER_UNIT.kilometers,
  },
];

export const CHARGE_RATES_PER_UNIT = CHARGES_PER_UNIT_OPTIONS.concat(
  CHARGES_PER_VOLUME_OPTIONS,
  CHARGES_PER_DISTANCE_OPTIONS
);

/* The following are used by `ChargeListItem.component.js` of the Agreement View */

type RateUnitOptionType = {
  key: string;
  label: { translationId: string };
  value: PER_UNIT | '';
};

export const CHARGE_RATE_UNIT_OPTIONS: RateUnitOptionType[] = [
  // These are all base units which are not volume or distance
  {
    key: 'notApplicable',
    label: { translationId: RATE_UNIT_TRANSLATIONS.notApplicable },
    value: '',
  },
  {
    key: 'day',
    label: { translationId: RATE_UNIT_TRANSLATIONS.day },
    value: PER_UNIT.day,
  },
];

export const CHARGE_RATE_UNIT_VOLUME_OPTIONS = [
  // These are all possible volume units, to be selected based on dealership settings
  {
    key: 'us-gal',
    label: { translationId: RATE_UNIT_TRANSLATIONS['us-gal'] },
    value: PER_UNIT.usgallons,
  },
  // UK-Gal is not expected to be used by any customers at the moment.
  // {
  //   key: 'uk-gal',
  //   label: { translationId: RATE_UNIT_TRANSLATIONS['uk-gal'] },
  //   value: PER_UNIT.ukgallons,
  // },
  {
    key: 'L',
    label: { translationId: RATE_UNIT_TRANSLATIONS.L },
    value: PER_UNIT.liters,
  },
  {
    key: 'eighths',
    label: { translationId: RATE_UNIT_TRANSLATIONS.eighths },
    value: PER_UNIT.eighths,
  },
];

export const CHARGE_RATE_UNIT_DISTANCE_OPTIONS = [
  // These are all possible distance units, to be selected based on dealership settings
  {
    key: 'mi',
    label: { translationId: RATE_UNIT_TRANSLATIONS.mi },
    value: PER_UNIT.miles,
  },
  {
    key: 'km',
    label: { translationId: RATE_UNIT_TRANSLATIONS.km },
    value: PER_UNIT.kilometers,
  },
];

export const CHARGE_RATE_UNITS = CHARGE_RATE_UNIT_OPTIONS.concat(
  CHARGE_RATE_UNIT_VOLUME_OPTIONS,
  CHARGE_RATE_UNIT_DISTANCE_OPTIONS
);
